import { Alert, Collapse, Typography } from 'antd'
import { SupportedJurisdictions, useJurisdictionProperties } from 'api'
import Container from 'components/Container'
import { useFormikContext } from 'formik'
import { FormItem, Select } from 'formik-antd'
import { useLocalization } from 'localization'
import { useEffect, useMemo, useState } from 'react'
import Skeleton from './skeleton'
import { ITaxesFormValues } from './types'

const JurisdictionProperties = () => {
	const { t } = useLocalization()
	const { values, setFieldValue } = useFormikContext<ITaxesFormValues>()
	const [innerJurisdiction, setInnerJurisdiction] = useState(values.jurisdiction)
	const { userProperties = [], isLoading, isFetching, isFetched } = useJurisdictionProperties(values.jurisdiction)

	// UK only rule https://www.gov.uk/hmrc-internal-manuals/cryptoassets-manual/crypto20250
	const shouldDisplayBusinessWarning = useMemo(
		() =>
			innerJurisdiction === SupportedJurisdictions.UK &&
			(values.properties?.['User_is_taxed_as_business_or_individual'] === 'Business' ||
				values.properties?.['User_is_taxed_as_investor_or_trader'] === 'Investor'),
		[values.properties, innerJurisdiction]
	)

	const shouldDisplayFIFOResetWarning = useMemo(
		() => values.method !== 'FIFO' && values.properties?.['User_is_taxed_as_investor_or_trader'] === 'Trader',
		[values.properties]
	)

	useEffect(() => {
		if (!isFetching) {
			if (innerJurisdiction && innerJurisdiction !== values.jurisdiction) {
				// reset properties to the 1st default
				userProperties.forEach(({ key, options }) => {
					setFieldValue(`properties.${key}`, options?.[0]?.value)
				})
				setInnerJurisdiction(values.jurisdiction)
			}
		}
	}, [values.jurisdiction, isFetching])

	if (!values.jurisdiction || userProperties.length === 0) return null

	return (
		<Collapse>
			<Collapse.Panel header={t('terms.properties')} key="1">
				<Container display="flex" flexDirection="column" space="sm" spaceDirection="vertical">
					<Typography.Text italic>{t('settings.taxes.propertyDescription')}</Typography.Text>
					{shouldDisplayBusinessWarning && <Alert type="warning" message={t('settings.taxes.businessWarning')} />}
					{shouldDisplayFIFOResetWarning && <Alert type="warning" message={t('settings.taxes.fifoDefaultReset')} />}
					{isLoading && !isFetched ? (
						<Skeleton />
					) : (
						<Container>
							{userProperties.map(({ title, key, options }) => (
								<FormItem key={key} name={`properties.${key}`} label={title}>
									<Select name={`properties.${key}`} options={options} placeholder={t('placeholders.property')} />
								</FormItem>
							))}
						</Container>
					)}
				</Container>
			</Collapse.Panel>
		</Collapse>
	)
}

export default JurisdictionProperties
